/**
 * Offer Promotion Widgets (Offer Engine Phase 2, international only)
 *
 * Promotes the active offer across the storefront. Reads the SAME source that prices
 * the Stripe charge (window.OfferEngine.getActiveOffer()), so country, schedule and
 * one-offer-at-a-time gating are inherited and promotion can never disagree with the
 * checkout. When no offer is active for the shopper's country this file renders
 * nothing and fetches nothing.
 *
 * Placements (each can be disabled per offer via the block's promo settings):
 *   1. Announcement strip  - slim sitewide bar below the announcement slider
 *   2. PDP offer card      - one cohesive, mobile-first card above the buy buttons:
 *        flat  = this product's exact checkout price (struck current price + gold
 *                discounted price, recomputed on variant change)
 *        tier  = the full spend ladder as compact rows
 *        bogo  = plain-words mechanics (which piece gets the discount)
 *      plus a trust row ("Applied automatically at checkout. No code needed.")
 *   3. Cart smart module   - tier progress stepper / BOGO nudge / flat confirmation
 *   4. BOGO toast          - once per session, when the first eligible item is added
 *
 * Copy rule: NO EM DASHES anywhere (merchant rule; enforced by unit tests).
 *
 * Structure mirrors assets/offer-engine.js: a PURE core (copy factory + tier math,
 * exported via UMD for Node unit tests) and a browser runtime (DOM mounting).
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api; // Node (unit tests import the pure core)
  }
  if (root) {
    root.OfferPromos = api;
    api._autoInit(root);
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // PURE CORE (no window/DOM access)
  // ---------------------------------------------------------------------------

  // Locale string tables. 'ar' is selected when the page html lang starts with
  // ar (Shopify sets it per locale); everything else uses English. Both tables
  // follow the merchant copy rules (no em dashes).
  var STRINGS = {
    en: {
      months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      trust: 'Applied automatically at checkout. No code needed.',
      eyebrow: 'Limited offer',
      ends: 'Ends {d} {m}',
      ann_flat: '{p}% off everything. Applied automatically at checkout.',
      ann_tiered: 'Spend more, save more: up to {p}% off. Applied automatically at checkout.',
      ann_bogo: 'Buy 1, get {p}% off your 2nd piece. No code needed.',
      cart_flat_chip: '{p}% off applied to your order.',
      toast_body: 'Lovely choice. Add a second piece and it is {p}% off, applied automatically.',
      toast_cta: 'Explore bestsellers',
      flat_headline: '{p}% off everything',
      flat_body: '{p}% comes off every piece at checkout.',
      price_label: 'your price at checkout',
      tiered_headline: 'Save up to {p}% on your order',
      bogo_headline: 'Buy 1, get the 2nd at {p}% off',
      bogo_body: 'Add any 2 pieces to your cart. The lower-priced piece is {p}% off at checkout.',
      tier_below: 'Add {amt} more to unlock {p}% off',
      tier_mid: '{p}% off unlocked. Add {amt} more for {p2}% off',
      tier_best: 'Best offer unlocked: {p}% off',
      bogo_need: 'Add 1 more piece and the 2nd is {p}% off.',
      bogo_done: '{p}% off your 2nd piece is applied.'
    },
    ar: {
      months: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
      trust: 'يُطبق تلقائياً عند الدفع. بدون كود خصم.',
      eyebrow: 'عرض لفترة محدودة',
      ends: 'ينتهي {d} {m}',
      ann_flat: 'خصم {p}% على كل القطع. يُطبق تلقائياً عند الدفع.',
      ann_tiered: 'كلما زاد طلبك زاد الخصم: حتى {p}%. يُطبق تلقائياً عند الدفع.',
      ann_bogo: 'اشتري قطعة واحصلي على خصم {p}% على القطعة الثانية. بدون كود خصم.',
      cart_flat_chip: 'تم تطبيق خصم {p}% على طلبك.',
      toast_body: 'اختيار رائع. أضيفي قطعة ثانية واحصلي على خصم {p}% عليها، يُطبق تلقائياً.',
      toast_cta: 'تسوقي الأكثر مبيعاً',
      flat_headline: 'خصم {p}% على كل القطع',
      flat_body: 'يُخصم {p}% من كل قطعة عند الدفع.',
      price_label: 'سعرك عند الدفع',
      tiered_headline: 'وفري حتى {p}% على طلبك',
      bogo_headline: 'اشتري قطعة والثانية بخصم {p}%',
      bogo_body: 'أضيفي أي قطعتين إلى السلة، والقطعة الأقل سعراً عليها خصم {p}% عند الدفع.',
      tier_below: 'أضيفي {amt} إضافية لفتح خصم {p}%',
      tier_mid: 'تم فتح خصم {p}%. أضيفي {amt} لخصم {p2}%',
      tier_best: 'تم فتح أفضل عرض: خصم {p}%',
      bogo_need: 'أضيفي قطعة أخرى واحصلي على خصم {p}% عليها.',
      bogo_done: 'تم تطبيق خصم {p}% على قطعتك الثانية.'
    }
  };

  function normLocale(locale) {
    return String(locale || 'en').toLowerCase().indexOf('ar') === 0 ? 'ar' : 'en';
  }
  function S(locale) { return STRINGS[normLocale(locale)]; }
  function fmt(tpl, vars) {
    return tpl.replace(/\{(\w+)\}/g, function (m, k) {
      return k in vars ? String(vars[k]) : m;
    });
  }

  function pct(offer) {
    var p = parseFloat(offer && offer.percent);
    return isFinite(p) && p > 0 ? Math.round(p) : 0;
  }

  function sortedTiers(offer) {
    var tiers = (offer && offer.tiers) || [];
    var clean = [];
    for (var i = 0; i < tiers.length; i++) {
      var min = parseFloat(tiers[i].min);
      var p = parseFloat(tiers[i].percent);
      if (isFinite(min) && min > 0 && isFinite(p) && p > 0) {
        clean.push({ min: min, percent: Math.round(p) });
      }
    }
    clean.sort(function (a, b) { return a.min - b.min; });
    return clean;
  }

  function maxTierPercent(offer) {
    var tiers = sortedTiers(offer);
    var max = 0;
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].percent > max) max = tiers[i].percent;
    }
    return max;
  }

  // Format a market-currency amount for copy. Remaining amounts are rounded UP
  // before calling this so we never understate what is left to add.
  function fmtAmount(symbol, value) {
    var v = Math.ceil(Number(value));
    if (!isFinite(v) || v < 0) v = 0;
    // Shared locale-aware formatter (handles "50 €" vs "€ 50" vs "AED 50");
    // the scraped symbol is only a fallback when MarketConfig is absent.
    if (typeof window !== 'undefined' && window.MarketConfig && typeof window.MarketConfig.formatDisplay === 'function') {
      return window.MarketConfig.formatDisplay(v, { trimWholeDecimals: true });
    }
    return (symbol || '$') + String(v);
  }

  /** 'YYYY-MM-DD' -> 'Ends 12 Aug' (localised). Empty string when absent/invalid. */
  function endDateLabel(end, locale) {
    var m = String(end || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return '';
    var month = parseInt(m[2], 10);
    var day = parseInt(m[3], 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) return '';
    var L = S(locale);
    return fmt(L.ends, { d: day, m: L.months[month - 1] });
  }

  /**
   * Checkout price preview for flat offers. Mirrors the engine's whole-unit
   * rounding (offer-engine.js roundUnit) so the preview equals the charge.
   */
  function flatPreview(percent, price) {
    var p = parseFloat(percent);
    var v = parseFloat(price);
    if (!isFinite(p) || p <= 0 || !isFinite(v) || v <= 0) return null;
    var now = Math.round(v * (1 - p / 100));
    if (now < 0) now = 0;
    return { was: v, now: now };
  }

  /**
   * Content for the PDP offer card. The runtime renders this; promo_pdp_text
   * overrides the headline. `tiers` is present only for tiered offers.
   */
  function pdpCard(offer, locale) {
    if (!offer) return null;
    var promo = offer.promo || {};
    var p = pct(offer);
    var L = S(locale);
    var card = {
      eyebrow: L.eyebrow,
      ends: endDateLabel(offer.end, locale),
      headline: '',
      body: '',
      trust: L.trust,
      priceLabel: L.price_label,
      tiers: null,
      wantsPrice: false
    };

    if (offer.type === 'flat_all') {
      card.headline = fmt(L.flat_headline, { p: p });
      card.body = fmt(L.flat_body, { p: p });
      card.wantsPrice = true; // runtime swaps body for an exact price preview when possible
    } else if (offer.type === 'tiered_spend') {
      var tiers = sortedTiers(offer);
      if (!tiers.length) return null;
      card.headline = fmt(L.tiered_headline, { p: maxTierPercent(offer) });
      card.tiers = tiers;
    } else if (offer.type === 'bogo_next') {
      card.headline = fmt(L.bogo_headline, { p: p });
      card.body = fmt(L.bogo_body, { p: p });
    } else {
      return null;
    }

    if (promo.pdp_text) card.headline = promo.pdp_text;
    return card;
  }

  /** Static promo copy for the strip, toast and flat cart chip (with overrides). */
  function promoCopy(offer, locale) {
    var promo = (offer && offer.promo) || {};
    var p = pct(offer);
    var L = S(locale);
    var out = { announcement: '', toastBody: '', toastCta: L.toast_cta, cartFlatChip: '' };
    if (!offer) return out;

    if (offer.type === 'flat_all') {
      out.announcement = fmt(L.ann_flat, { p: p });
      out.cartFlatChip = fmt(L.cart_flat_chip, { p: p });
    } else if (offer.type === 'tiered_spend') {
      out.announcement = fmt(L.ann_tiered, { p: maxTierPercent(offer) });
    } else if (offer.type === 'bogo_next') {
      out.announcement = fmt(L.ann_bogo, { p: p });
      out.toastBody = fmt(L.toast_body, { p: p });
    }

    if (promo.headline) out.announcement = promo.headline;
    if (promo.toast_text) out.toastBody = promo.toast_text;
    return out;
  }

  /**
   * Tier progress for the cart stepper.
   * Returns { tiers:[{min,percent,reached,ratio}], current, next, remaining,
   *           fillRatio, best } or null when the offer has no usable tiers.
   */
  function tierProgress(offer, subtotal) {
    var tiers = sortedTiers(offer);
    if (!tiers.length) return null;
    var sub = Number(subtotal);
    if (!isFinite(sub) || sub < 0) sub = 0;

    var top = tiers[tiers.length - 1].min;
    var current = null;
    var next = null;
    for (var i = 0; i < tiers.length; i++) {
      if (sub >= tiers[i].min) current = tiers[i];
      else if (!next) next = tiers[i];
      tiers[i] = {
        min: tiers[i].min,
        percent: tiers[i].percent,
        reached: sub >= tiers[i].min,
        ratio: tiers[i].min / top
      };
    }
    return {
      tiers: tiers,
      current: current,
      next: next,
      remaining: next ? Math.max(0, next.min - sub) : 0,
      fillRatio: Math.max(0, Math.min(1, sub / top)),
      best: !!current && !next
    };
  }

  /** Headline for the tier stepper. `symbol` is the market currency prefix. */
  function tierHeadline(progress, symbol, locale) {
    if (!progress) return '';
    var L = S(locale);
    if (progress.best) {
      return fmt(L.tier_best, { p: progress.current.percent });
    }
    var amt = fmtAmount(symbol, progress.remaining);
    if (progress.current) {
      return fmt(L.tier_mid, { p: progress.current.percent, amt: amt, p2: progress.next.percent });
    }
    return fmt(L.tier_below, { amt: amt, p: progress.next.percent });
  }

  /** BOGO cart state from the count of eligible units. */
  function bogoState(units) {
    var u = Math.floor(Number(units));
    if (!isFinite(u) || u <= 0) return null;
    return u === 1 ? 'need_more' : 'satisfied';
  }

  function bogoCartCopy(offer, state, locale) {
    var p = pct(offer);
    var L = S(locale);
    if (state === 'need_more') return fmt(L.bogo_need, { p: p });
    if (state === 'satisfied') return fmt(L.bogo_done, { p: p });
    return '';
  }

  // ---------------------------------------------------------------------------
  // BROWSER RUNTIME
  // ---------------------------------------------------------------------------

  var CSS = [
    // Announcement strip
    '.offer-promo-strip{background:#14100c;color:#e8dcc4;text-align:center;padding:9px 16px;',
    'font-size:12px;letter-spacing:.08em;text-transform:uppercase;border-top:1px solid rgba(201,168,106,.35);',
    'border-bottom:1px solid rgba(201,168,106,.35);position:relative;overflow:hidden;}',
    '.offer-promo-strip::after{content:"";position:absolute;top:0;left:-60%;width:40%;height:100%;',
    'background:linear-gradient(105deg,transparent,rgba(201,168,106,.14),transparent);',
    'animation:offerSheen 6s ease-in-out infinite;}',
    '@keyframes offerSheen{0%,70%{left:-60%}100%{left:120%}}',

    // PDP offer card (mobile-first, compact: eyebrow row, content row, trust row)
    '.offer-promo-card{border:1px solid rgba(201,168,106,.45);border-radius:10px;',
    'padding:10px 12px;margin:14px 0 0;background:linear-gradient(180deg,rgba(201,168,106,.07),rgba(201,168,106,.02));}',
    '.offer-promo-card__eyebrow{display:flex;align-items:center;justify-content:space-between;',
    'font-size:10px;letter-spacing:.16em;text-transform:uppercase;color:#c9a86a;margin:0 0 6px;}',
    '.offer-promo-card__eyebrow .offer-promo-card__tag{display:inline-flex;align-items:center;gap:6px;}',
    '.offer-promo-card__eyebrow svg{width:11px;height:11px;}',
    '.offer-promo-card__ends{opacity:.85;letter-spacing:.1em;}',
    '.offer-promo-card__headline{font-size:14px;font-weight:600;line-height:1.35;margin:0;}',
    '.offer-promo-card__body{font-size:12px;line-height:1.5;opacity:.75;margin:4px 0 0;}',
    '.offer-promo-card__price{display:flex;align-items:baseline;gap:10px;margin:6px 0 0;flex-wrap:wrap;}',
    '.offer-promo-card__price s{font-size:12.5px;opacity:.55;}',
    '.offer-promo-card__price strong{font-size:16px;color:#c9a86a;font-weight:600;}',
    '.offer-promo-card__price span{font-size:11px;opacity:.65;}',
    '.offer-promo-card .offer-promo-cart__track{margin:16px 12px 36px;}',
    '.offer-promo-card__trust{display:flex;align-items:center;gap:6px;font-size:10.5px;',
    'letter-spacing:.02em;color:#c9a86a;opacity:.9;margin:8px 0 0;}',
    '.offer-promo-card__trust svg{width:11px;height:11px;flex:0 0 auto;}',

    // Cart module
    '.offer-promo-cart{margin:0 0 16px;padding:12px 14px;border:1px solid rgba(201,168,106,.4);',
    'border-radius:6px;font-size:12.5px;line-height:1.5;}',
    '.offer-promo-cart__headline{margin:0 0 10px;letter-spacing:.02em;}',
    '.offer-promo-cart__headline strong{color:#c9a86a;}',
    '.offer-promo-cart--unlocked .offer-promo-cart__fill{animation:offerFillSheen 2.2s ease-in-out 2;}',
    '.offer-promo-cart__track{position:relative;height:3px;background:rgba(201,168,106,.22);',
    'border-radius:2px;margin:14px 10px 34px;}',
    '.offer-promo-cart__fill{position:absolute;left:0;top:0;height:100%;border-radius:2px;',
    'background:linear-gradient(90deg,#a5854e,#d9bc7f);transition:width .6s ease;}',
    '@keyframes offerFillSheen{0%,100%{filter:brightness(1)}50%{filter:brightness(1.45)}}',
    '.offer-promo-cart__dot{position:absolute;top:50%;width:11px;height:11px;border-radius:50%;',
    'transform:translate(-50%,-50%);background:#1a1a1a;border:1.5px solid rgba(201,168,106,.55);}',
    '.offer-promo-cart__dot--reached{background:#c9a86a;border-color:#c9a86a;}',
    '.offer-promo-cart__dot span{position:absolute;top:13px;left:50%;transform:translateX(-50%);',
    'text-align:center;white-space:nowrap;}',
    '.offer-promo-cart__dot em{display:block;font-style:normal;font-size:10px;letter-spacing:.04em;opacity:.8;}',
    '.offer-promo-cart__dot small{display:block;font-size:9.5px;letter-spacing:.02em;opacity:.5;margin-top:1px;}',

    // Toast
    '.offer-promo-toast{position:fixed;bottom:24px;right:24px;z-index:9999;max-width:340px;',
    'background:#14100c;color:#ece2cd;border:1px solid rgba(201,168,106,.5);border-radius:8px;',
    'padding:16px 38px 16px 18px;font-size:13px;line-height:1.55;box-shadow:0 10px 30px rgba(0,0,0,.45);',
    'transform:translateY(16px);opacity:0;transition:transform .35s ease,opacity .35s ease;}',
    '.offer-promo-toast--visible{transform:translateY(0);opacity:1;}',
    '.offer-promo-toast a{color:#c9a86a;text-decoration:underline;text-underline-offset:3px;}',
    '.offer-promo-toast__close{position:absolute;top:8px;right:12px;cursor:pointer;opacity:.6;',
    'font-size:16px;line-height:1;background:none;border:none;color:inherit;padding:4px;}',
    '@media (max-width:560px){.offer-promo-toast{left:16px;right:16px;bottom:16px;max-width:none;}}'
  ].join('');

  var TAG_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
    '<path d="M20.6 13.4 11 3.8H4v7l9.6 9.6a2 2 0 0 0 2.8 0l4.2-4.2a2 2 0 0 0 0-2.8Z"/><circle cx="7.5" cy="7.5" r="1" fill="currentColor"/></svg>';
  var CHECK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true">' +
    '<path d="m4 12.5 5 5L20 6.5"/></svg>';

  function pageLocale() {
    if (typeof document === 'undefined') return 'en';
    return normLocale(document.documentElement.lang || 'en');
  }

  var runtime = {
    locale: 'en',
    offer: null,
    copy: null,
    promoFlags: null,
    cartBusy: false,
    cartScheduled: false,
    observer: null
  };

  function flag(name) {
    // Placement toggles default ON; only an explicit false disables.
    return !runtime.promoFlags || runtime.promoFlags[name] !== false;
  }

  function injectCSS() {
    if (document.getElementById('offer-promos-css')) return;
    var style = document.createElement('style');
    style.id = 'offer-promos-css';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  function convertToMarketCurrency(priceInCents) {
    if (window.WebhookHelper && typeof window.WebhookHelper.convertToMarketCurrency === 'function') {
      return window.WebhookHelper.convertToMarketCurrency(priceInCents);
    }
    if (window.MarketConfig && typeof window.MarketConfig.convertPrice === 'function') {
      return window.MarketConfig.convertPrice(priceInCents);
    }
    return null;
  }

  function roundUpPrice(price) {
    if (window.WebhookHelper && typeof window.WebhookHelper.roundUpPrice === 'function') {
      return window.WebhookHelper.roundUpPrice(price);
    }
    return Math.ceil(price);
  }

  function isMtoItem(item) {
    var p = item && item.properties;
    // Only the plating service FEE line is excluded from offers; a product
    // carrying the '18kt Gold plating: Yes' display property is a normal,
    // offer-eligible product (the fee line carries _Service).
    return !!(p && p._Service);
  }

  // Eligible units + eligible market-currency subtotal, mirroring the charge basis.
  function cartBasis(cart) {
    var units = 0;
    var subtotal = 0;
    if (!cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (runtime.offer.exclude_mto && isMtoItem(it)) continue;
      var converted = convertToMarketCurrency(it.final_price);
      if (converted === null) return null;
      units += it.quantity;
      subtotal += roundUpPrice(converted) * it.quantity;
    }
    return { units: units, subtotal: subtotal };
  }

  // Parse "AED 1,234.00" / "$ 127.00" / "1.234,00 €" into { symbol, value };
  // null when unparseable. The numeric value comes from the shared
  // MarketConfig.parseDisplay, which knows the current locale's separators;
  // the inline regex is only the fallback when MarketConfig is absent.
  function parsePriceText(text) {
    var m = String(text || '').trim().match(/^([^\d]*)([\d.,]+)/);
    if (!m) return null;
    var value;
    if (typeof window !== 'undefined' && window.MarketConfig && typeof window.MarketConfig.parseDisplay === 'function') {
      value = window.MarketConfig.parseDisplay(text);
    } else {
      value = parseFloat(m[2].replace(/,/g, ''));
    }
    if (!isFinite(value) || value === null || value <= 0) return null;
    // Sign-style symbols ($, £, €) hug the number ("$43"); letter codes keep
    // one space ("AED 100"). The theme renders "$ 190.00", so trim its space.
    var symbol = m[1].trim();
    if (!symbol) symbol = '$';
    else if (/[A-Za-z]$/.test(symbol)) symbol += ' ';
    return { symbol: symbol, value: value };
  }

  function displayedProductPrice() {
    var el = document.querySelector('[data-product-price]');
    if (!el) return null;
    var parsed = parsePriceText(el.textContent);
    if (!parsed) return null;
    // Plating no longer alters this element (the price shows the variant's own
    // price and addonValue() reports 0), but keep the subtraction so offer math
    // stays on the base piece price even if that display behavior returns.
    var addon = (window.DuavaaPlating && typeof window.DuavaaPlating.addonValue === 'function')
      ? Number(window.DuavaaPlating.addonValue()) || 0
      : 0;
    if (addon > 0) parsed = { symbol: parsed.symbol, value: Math.max(0, parsed.value - addon) };
    return parsed;
  }

  function currencySymbol() {
    var el = document.querySelector('[data-cart-total]');
    if (el) {
      var parsed = parsePriceText(el.textContent);
      if (parsed) return parsed.symbol;
    }
    var pdp = displayedProductPrice();
    return pdp ? pdp.symbol : '$';
  }

  function fmtMoney(symbol, value) {
    var v = Number(value);
    // Shared locale-aware formatter; the scraped symbol is only a fallback.
    if (typeof window !== 'undefined' && window.MarketConfig && typeof window.MarketConfig.formatDisplay === 'function') {
      return window.MarketConfig.formatDisplay(v, { trimWholeDecimals: true });
    }
    var text = (v % 1 === 0) ? String(v) : v.toFixed(2);
    return symbol + text;
  }

  // ---- 1. Announcement strip ----
  function mountStrip() {
    if (!flag('announcement') || !runtime.copy.announcement) return;
    if (document.querySelector('.offer-promo-strip')) return;
    var strip = document.createElement('div');
    strip.className = 'offer-promo-strip';
    strip.textContent = runtime.copy.announcement;
    var anchor = document.querySelector('[data-announcement-wrapper]');
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(strip, anchor.nextSibling);
    } else {
      document.body.insertBefore(strip, document.body.firstChild);
    }
  }

  // Shared tier stepper (track + fill + a dot per tier). Used by both the cart
  // module and the PDP offer card so the offer has one visual language sitewide.
  function buildTierStepper(progress, symbol) {
    var track = document.createElement('div');
    track.className = 'offer-promo-cart__track';
    var fill = document.createElement('div');
    fill.className = 'offer-promo-cart__fill';
    fill.style.width = (progress.fillRatio * 100).toFixed(1) + '%';
    track.appendChild(fill);
    for (var i = 0; i < progress.tiers.length; i++) {
      var t = progress.tiers[i];
      var dot = document.createElement('div');
      dot.className = 'offer-promo-cart__dot' + (t.reached ? ' offer-promo-cart__dot--reached' : '');
      dot.style.left = (t.ratio * 100).toFixed(1) + '%';
      // Two-line milestone label: percent over the spend threshold, so the
      // ladder is self-explanatory without reading the headline.
      var label = document.createElement('span');
      var pctLine = document.createElement('em');
      pctLine.textContent = t.percent + '%';
      var amtLine = document.createElement('small');
      amtLine.textContent = fmtMoney(symbol || '$', t.min);
      label.appendChild(pctLine);
      label.appendChild(amtLine);
      dot.appendChild(label);
      track.appendChild(dot);
    }
    return track;
  }

  // ---- 2. PDP offer card ----
  function buildPdpCard(basis) {
    var card = pdpCard(runtime.offer, runtime.locale);
    if (!card) return null;

    var el = document.createElement('div');
    el.className = 'offer-promo-card';

    var eyebrow = document.createElement('div');
    eyebrow.className = 'offer-promo-card__eyebrow';
    var tag = document.createElement('span');
    tag.className = 'offer-promo-card__tag';
    tag.innerHTML = TAG_SVG + '<span>' + card.eyebrow + '</span>';
    eyebrow.appendChild(tag);
    if (card.ends) {
      var ends = document.createElement('span');
      ends.className = 'offer-promo-card__ends';
      ends.textContent = card.ends;
      eyebrow.appendChild(ends);
    }
    el.appendChild(eyebrow);

    // Headline: tiered cards show a LIVE cart-aware line ("Add $23 more to unlock
    // 10% off"), same voice as the cart stepper. A promo_pdp_text override wins.
    var promoOverride = runtime.offer.promo && runtime.offer.promo.pdp_text;
    var progress = null;
    var head = document.createElement('p');
    head.className = 'offer-promo-card__headline';
    if (card.tiers && !promoOverride) {
      progress = tierProgress(runtime.offer, basis ? basis.subtotal : 0);
      head.textContent = progress ? tierHeadline(progress, currencySymbol(), runtime.locale) : card.headline;
    } else {
      head.textContent = card.headline;
    }
    el.appendChild(head);
    if (card.tiers && !progress) {
      progress = tierProgress(runtime.offer, basis ? basis.subtotal : 0);
    }

    // Flat: exact checkout price for THIS product when the displayed price is parseable.
    var priceShown = false;
    if (card.wantsPrice) {
      var price = displayedProductPrice();
      var preview = price ? flatPreview(runtime.offer.percent, price.value) : null;
      if (preview && preview.now < preview.was) {
        var row = document.createElement('div');
        row.className = 'offer-promo-card__price';
        var was = document.createElement('s');
        was.textContent = fmtMoney(price.symbol, preview.was);
        var now = document.createElement('strong');
        now.textContent = fmtMoney(price.symbol, preview.now);
        var label = document.createElement('span');
        label.textContent = card.priceLabel;
        row.appendChild(was);
        row.appendChild(now);
        row.appendChild(label);
        el.appendChild(row);
        priceShown = true;
      }
    }

    if (card.body && !priceShown) {
      var body = document.createElement('p');
      body.className = 'offer-promo-card__body';
      body.textContent = card.body;
      el.appendChild(body);
    }

    if (progress) {
      // Same stepper as the cart module: one visual language for the offer everywhere.
      if (progress.best) el.classList.add('offer-promo-cart--unlocked');
      el.appendChild(buildTierStepper(progress, currencySymbol()));
    }

    var trust = document.createElement('div');
    trust.className = 'offer-promo-card__trust';
    trust.innerHTML = CHECK_SVG + '<span>' + card.trust + '</span>';
    el.appendChild(trust);

    return el;
  }

  function renderPdpCard(submit, basis) {
    // Paused observer: our own card swap must never re-trigger the cart observer
    // (which now also refreshes this card), or we would loop.
    withObserverPaused(function () {
      var existing = submit.querySelector('.offer-promo-card');
      if (existing) existing.parentNode.removeChild(existing);
      var card = buildPdpCard(basis);
      // Below the buy buttons per merchant direction: the CTA stays highest,
      // the offer reinforces right underneath it.
      if (card) submit.appendChild(card);
    });
  }

  function mountPdp() {
    if (!flag('pdp')) return;
    var submit = document.querySelector('.product__submit');
    if (!submit) return; // not a product page
    // MTO advance flow: since the theme switched to CSS-driven button
    // visibility, the advance button is ALWAYS in the DOM (hidden inside
    // [data-mto-block] unless the variant is sold out) — gate on it actually
    // being shown, not on it existing. When the shopper switches to a
    // sold-out variant, also drop the already-mounted card.
    var mtoButton = document.querySelector('[data-preorder-advance-button]');
    if (mtoButton && mtoButton.offsetParent !== null) {
      withObserverPaused(function () {
        var existing = submit.querySelector('.offer-promo-card');
        if (existing) existing.parentNode.removeChild(existing);
      });
      return;
    }

    if (runtime.offer.type === 'tiered_spend') {
      // Cart-aware card: the stepper fill + headline reflect the live cart, exactly
      // like the cart module, and re-render on cart changes.
      fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          renderPdpCard(submit, cartBasis(cart) || { units: 0, subtotal: 0 });
        })
        .catch(function () {
          renderPdpCard(submit, { units: 0, subtotal: 0 });
        });
    } else {
      renderPdpCard(submit, null);
    }
  }

  // ---- 3. Cart smart module ----
  function buildCartModule(basis) {
    var offer = runtime.offer;
    var wrap = document.createElement('div');
    wrap.className = 'offer-promo-cart';

    if (offer.type === 'tiered_spend') {
      var progress = tierProgress(offer, basis.subtotal);
      if (!progress) return null;
      if (progress.best) wrap.classList.add('offer-promo-cart--unlocked');

      var head = document.createElement('p');
      head.className = 'offer-promo-cart__headline';
      var headline = tierHeadline(progress, currencySymbol(), runtime.locale);
      if (progress.best) {
        head.innerHTML = '<strong>' + headline + '</strong>';
      } else {
        head.textContent = headline;
      }
      wrap.appendChild(head);

      wrap.appendChild(buildTierStepper(progress, currencySymbol()));
      return wrap;
    }

    if (offer.type === 'bogo_next') {
      var state = bogoState(basis.units);
      if (!state) return null;
      var text = bogoCartCopy(offer, state, runtime.locale);
      var p1 = document.createElement('p');
      p1.className = 'offer-promo-cart__headline';
      if (state === 'satisfied') {
        p1.innerHTML = '<strong>' + text + '</strong>';
      } else {
        p1.textContent = text;
      }
      p1.style.margin = '0';
      wrap.appendChild(p1);
      return wrap;
    }

    // flat_all: small confirmation only (the discount row shows the amount)
    if (runtime.copy.cartFlatChip && basis.units > 0) {
      var p2 = document.createElement('p');
      p2.className = 'offer-promo-cart__headline';
      p2.innerHTML = '<strong>' + runtime.copy.cartFlatChip + '</strong>';
      p2.style.margin = '0';
      wrap.appendChild(p2);
      return wrap;
    }
    return null;
  }

  function renderCartModules() {
    if (!flag('cart')) return;
    var totals = document.querySelectorAll('[data-cart-total]');
    if (!totals.length) return;

    runtime.cartBusy = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var basis = cartBasis(cart);
        withObserverPaused(function () {
          // Idempotent re-render: clear previous modules, then mount fresh ones.
          var old = document.querySelectorAll('.offer-promo-cart');
          for (var i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);
          if (!basis || basis.units <= 0) return;

          var seen = [];
          for (var j = 0; j < totals.length; j++) {
            var row = totals[j].closest('.cart__total');
            var container = row ? row.parentNode : null;
            if (!container || seen.indexOf(container) !== -1) continue;
            seen.push(container);
            var module = buildCartModule(basis);
            if (module) container.insertBefore(module, container.firstChild);
          }
        });
      })
      .catch(function () { /* leave the cart untouched on any error */ })
      .then(function () { runtime.cartBusy = false; });
  }

  function withObserverPaused(fn) {
    if (runtime.observer) runtime.observer.disconnect();
    try { fn(); } finally {
      if (runtime.observer) {
        runtime.observer.takeRecords();
        runtime.observer.observe(document.body, { childList: true, subtree: true, characterData: true });
      }
    }
  }

  function scheduleCart() {
    if (runtime.cartScheduled) return;
    runtime.cartScheduled = true;
    setTimeout(function () {
      runtime.cartScheduled = false;
      if (!runtime.cartBusy) renderCartModules();
      // Tiered PDP card is cart-aware: keep its fill + headline in sync too.
      if (runtime.offer && runtime.offer.type === 'tiered_spend') mountPdp();
    }, 150);
  }

  // ---- 4. BOGO toast ----
  var PREV_UNITS_KEY = 'duavaaOfferPrevUnits';
  var TOAST_SHOWN_KEY = 'duavaaOfferToastShown';

  function showToast() {
    if (document.querySelector('.offer-promo-toast')) return;
    var promo = runtime.offer.promo || {};
    var toast = document.createElement('div');
    toast.className = 'offer-promo-toast';

    var body = document.createElement('p');
    body.style.margin = '0';
    body.textContent = runtime.copy.toastBody;
    toast.appendChild(body);

    var link = document.createElement('a');
    var root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
    link.href = promo.link || (root.replace(/\/$/, '') + '/collections/bestsellers');
    link.textContent = runtime.copy.toastCta;
    link.style.display = 'inline-block';
    link.style.marginTop = '8px';
    toast.appendChild(link);

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'offer-promo-toast__close';
    close.setAttribute('aria-label', 'Dismiss');
    close.textContent = '×';
    close.addEventListener('click', function () { hide(); });
    toast.appendChild(close);

    document.body.appendChild(toast);
    requestAnimationFrame(function () { toast.classList.add('offer-promo-toast--visible'); });

    var timer = setTimeout(hide, 8000);
    function hide() {
      clearTimeout(timer);
      toast.classList.remove('offer-promo-toast--visible');
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 400);
    }
  }

  function checkToast() {
    if (!flag('toast') || runtime.offer.type !== 'bogo_next' || !runtime.copy.toastBody) return;
    if (sessionStorage.getItem(TOAST_SHOWN_KEY)) return;

    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var basis = cartBasis(cart);
        if (!basis) return;
        var prev = parseInt(sessionStorage.getItem(PREV_UNITS_KEY) || '0', 10);
        if (prev === 0 && basis.units > 0) {
          sessionStorage.setItem(TOAST_SHOWN_KEY, '1');
          showToast();
        }
        sessionStorage.setItem(PREV_UNITS_KEY, String(basis.units));
      })
      .catch(function () {});
  }

  function seedPrevUnits() {
    // Record the current unit count without toasting, so a returning visitor with
    // items already in the cart is not greeted by the toast.
    if (sessionStorage.getItem(PREV_UNITS_KEY) !== null) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var basis = cartBasis(cart);
        sessionStorage.setItem(PREV_UNITS_KEY, String(basis ? basis.units : 0));
      })
      .catch(function () {});
  }

  // ---- init ----
  function init() {
    if (!window.OfferEngine || typeof window.OfferEngine.getActiveOffer !== 'function') return;
    var offer = window.OfferEngine.getActiveOffer();
    if (!offer) return;

    runtime.offer = offer;
    runtime.locale = pageLocale();
    runtime.promoFlags = offer.promo || null;
    runtime.copy = promoCopy(offer, runtime.locale);

    injectCSS();
    mountStrip();
    mountPdp();

    // Variant switches change the displayed price; refresh the card's price preview.
    document.addEventListener('theme:variant:change', function () {
      setTimeout(mountPdp, 50);
    });

    // Cart module: initial render + re-render on cart changes (theme events + DOM fallback)
    if (flag('cart')) {
      renderCartModules();
      if (typeof MutationObserver !== 'undefined') {
        runtime.observer = new MutationObserver(function (mutations) {
          for (var i = 0; i < mutations.length; i++) {
            var t = mutations[i].target;
            if (t && t.closest && t.closest('.offer-promo-cart, .offer-promo-toast, .offer-promo-card')) continue;
            // Only react when a cart totals area exists on the page
            if (document.querySelector('[data-cart-total]')) scheduleCart();
            return;
          }
        });
        runtime.observer.observe(document.body, { childList: true, subtree: true, characterData: true });
      }
    }

    ['theme:cart:add', 'theme:cart:update', 'theme:cart:refresh', 'theme:cart:change'].forEach(function (ev) {
      document.addEventListener(ev, function () {
        scheduleCart();
        if (ev === 'theme:cart:add') setTimeout(checkToast, 250);
      });
    });

    if (runtime.offer.type === 'bogo_next' && flag('toast')) seedPrevUnits();
  }

  function _autoInit(win) {
    if (!win.document) return;
    if (win.document.readyState === 'loading') {
      win.document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  return {
    // pure core (unit tested)
    promoCopy: promoCopy,
    pdpCard: pdpCard,
    flatPreview: flatPreview,
    endDateLabel: endDateLabel,
    tierProgress: tierProgress,
    tierHeadline: tierHeadline,
    bogoState: bogoState,
    bogoCartCopy: bogoCartCopy,
    fmtAmount: fmtAmount,
    maxTierPercent: maxTierPercent,
    // runtime
    _autoInit: _autoInit,
    _init: init
  };
});
